/**
 * Native Theme Context and Provider
 *
 * Theme management for React Native applications.
 * Does not depend on DOM APIs (document, window.matchMedia).
 *
 * @example
 * ```tsx
 * import AsyncStorage from '@react-native-async-storage/async-storage';
 * import { NativeThemeProvider, useTheme } from '@tonnie/design-tokens/theme';
 *
 * function App() {
 *   return (
 *     <NativeThemeProvider
 *       appId="citizen"
 *       asyncStorage={AsyncStorage}
 *       systemColorScheme={useColorScheme()}
 *       enableSystem
 *     >
 *       <AppContent />
 *     </NativeThemeProvider>
 *   );
 * }
 * ```
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  type ReactNode,
} from 'react';

import type { Theme, ThemeMode, IOrganizationTheme, IStorageAdapter, IStorageKeys } from './types';

import {
  createStorageKeys,
  loadThemeMode,
  saveThemeMode,
  loadOrganizationTheme,
  saveOrganizationTheme,
} from './storage';

import { createNativeStorageAdapter, type IAsyncStorage } from './native-storage';

// ============================================================================
// TYPES
// ============================================================================

export interface INativeThemeContextValue {
  /** Current resolved theme (light or dark) */
  theme: Theme;
  /** Current theme mode setting */
  themeMode: ThemeMode;
  /** Set the theme mode */
  setThemeMode: (mode: ThemeMode) => void;
  /** Toggle between light and dark themes */
  toggleTheme: () => void;
  /** Whether theme switching is enabled */
  switchable: boolean;
  /** Whether the current theme is dark */
  isDark: boolean;
  /** Whether the current theme is light */
  isLight: boolean;
  /** Organization-specific theme overrides */
  organizationTheme?: IOrganizationTheme;
  /** Apply a custom organization theme */
  setOrganizationTheme: (theme: IOrganizationTheme | undefined) => void;
  /** Whether the provider has finished initializing */
  isInitialized: boolean;
}

export interface INativeThemeProviderProps {
  children: ReactNode;
  /** Unique app identifier for namespaced storage */
  appId: string;
  /** AsyncStorage instance from @react-native-async-storage/async-storage */
  asyncStorage: IAsyncStorage;
  /** System color scheme from useColorScheme() hook */
  systemColorScheme?: 'light' | 'dark' | null;
  /** Default theme to use when no preference is stored */
  defaultTheme?: Theme;
  /** Whether to allow theme switching */
  switchable?: boolean;
  /** Whether to use system preference */
  enableSystem?: boolean;
  /** Whether to enable custom/organization themes */
  enableCustom?: boolean;
  /** Initial organization theme */
  initialOrganizationTheme?: IOrganizationTheme;
  /** Callback when theme changes (for Appearance.setColorScheme) */
  onThemeChange?: (theme: Theme) => void;
}

// ============================================================================
// CONTEXT
// ============================================================================

const NativeThemeContext = createContext<INativeThemeContextValue | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

export function NativeThemeProvider({
  children,
  appId,
  asyncStorage,
  systemColorScheme,
  defaultTheme = 'light',
  switchable = true,
  enableSystem = true,
  enableCustom = false,
  initialOrganizationTheme,
  onThemeChange,
}: INativeThemeProviderProps) {
  // Refs for stable references
  const adapterRef = useRef<IStorageAdapter>(createNativeStorageAdapter(asyncStorage));
  const keysRef = useRef<IStorageKeys>(createStorageKeys(appId));

  // State
  const [isInitialized, setIsInitialized] = useState(false);
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    // Use defaultTheme as initial mode; enableSystem only gates availability
    return defaultTheme;
  });
  const [organizationTheme, setOrganizationThemeState] = useState<IOrganizationTheme | undefined>(
    initialOrganizationTheme
  );

  // Get system theme (from React Native's useColorScheme)
  const getSystemTheme = useCallback((): Theme => {
    return systemColorScheme === 'dark' ? 'dark' : 'light';
  }, [systemColorScheme]);

  // Compute resolved theme
  const theme = useMemo<Theme>(() => {
    if (themeMode === 'system') {
      return getSystemTheme();
    }
    if (themeMode === 'custom') {
      return 'light';
    }
    return themeMode as Theme;
  }, [themeMode, getSystemTheme]);

  // Derived states
  const isDark = theme === 'dark';
  const isLight = theme === 'light';

  // Load stored preferences on mount
  useEffect(() => {
    const loadPreferences = async () => {
      const adapter = adapterRef.current;
      const keys = keysRef.current;

      // Use defaultTheme as fallback; enableSystem only gates availability
      const defaultMode: ThemeMode = defaultTheme;
      const storedMode = await loadThemeMode(adapter, keys, defaultMode);

      // Validate mode is allowed
      let validMode = storedMode;
      if (storedMode === 'system' && !enableSystem) {
        validMode = defaultTheme;
      }
      if (storedMode === 'custom' && !enableCustom) {
        validMode = defaultTheme;
      }

      setThemeModeState(validMode);

      // Load organization theme if enabled
      if (enableCustom && !initialOrganizationTheme) {
        const storedOrgTheme = await loadOrganizationTheme(adapter, keys);
        if (storedOrgTheme) {
          setOrganizationThemeState(storedOrgTheme);
        }
      }

      setIsInitialized(true);
    };

    loadPreferences();
  }, [defaultTheme, enableSystem, enableCustom, initialOrganizationTheme]);

  // Notify parent when theme changes (for Appearance.setColorScheme)
  useEffect(() => {
    if (isInitialized && onThemeChange) {
      onThemeChange(theme);
    }
  }, [theme, isInitialized, onThemeChange]);

  // Update theme when system preference changes
  useEffect(() => {
    if (themeMode === 'system' && isInitialized) {
      // Theme will be recalculated via useMemo
    }
  }, [systemColorScheme, themeMode, isInitialized]);

  // Set theme mode with persistence
  const setThemeMode = useCallback(
    async (mode: ThemeMode) => {
      if (mode === 'system' && !enableSystem) {
        console.warn('[NativeThemeProvider] System mode is not enabled');
        return;
      }
      if (mode === 'custom' && !enableCustom) {
        console.warn('[NativeThemeProvider] Custom mode is not enabled');
        return;
      }

      setThemeModeState(mode);
      await saveThemeMode(adapterRef.current, keysRef.current, mode);
    },
    [enableSystem, enableCustom]
  );

  // Toggle between light and dark
  const toggleTheme = useCallback(() => {
    if (!switchable) return;

    const newMode = theme === 'light' ? 'dark' : 'light';
    setThemeMode(newMode);
  }, [switchable, theme, setThemeMode]);

  // Set organization theme
  const setOrganizationTheme = useCallback(
    async (newTheme: IOrganizationTheme | undefined) => {
      if (!enableCustom) {
        console.warn('[NativeThemeProvider] Custom mode is not enabled');
        return;
      }

      setOrganizationThemeState(newTheme);
      await saveOrganizationTheme(adapterRef.current, keysRef.current, newTheme);
    },
    [enableCustom]
  );

  // Context value
  const value = useMemo<INativeThemeContextValue>(
    () => ({
      theme,
      themeMode,
      setThemeMode,
      toggleTheme,
      switchable,
      isDark,
      isLight,
      organizationTheme,
      setOrganizationTheme,
      isInitialized,
    }),
    [
      theme,
      themeMode,
      setThemeMode,
      toggleTheme,
      switchable,
      isDark,
      isLight,
      organizationTheme,
      setOrganizationTheme,
      isInitialized,
    ]
  );

  return <NativeThemeContext.Provider value={value}>{children}</NativeThemeContext.Provider>;
}

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook to access native theme context
 */
export function useNativeTheme(): INativeThemeContextValue {
  const context = useContext(NativeThemeContext);
  if (!context) {
    throw new Error('useNativeTheme must be used within a NativeThemeProvider');
  }
  return context;
}

// ============================================================================
// EXPORTS
// ============================================================================

export { NativeThemeContext };
